import { SaveOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Form, Input, Row } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@tms/shared';

import { api } from '@/api/client';
import { useApiMutation } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';

import { SectionHeader } from './SidePanel';
import type { EmployeeDetail } from './types';

/** Как связаться с сотрудником и с кем — если с ним что-то случилось. */
export function ContactsSection({ employee }: { employee: EmployeeDetail }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [form] = Form.useForm();
  const canEdit = can(PERMISSIONS.EMPLOYEE_UPDATE);
  const canSensitive = can(PERMISSIONS.EMPLOYEE_SENSITIVE_READ);

  useEffect(() => {
    form.setFieldsValue({
      phone: employee.phone ?? '',
      email: employee.email ?? '',
      address: employee.address ?? '',
      emergencyContactName: employee.emergencyContactName ?? '',
      emergencyContactPhone: employee.emergencyContactPhone ?? '',
    });
  }, [employee, form]);

  const save = useApiMutation(
    async (values: Record<string, string>) => {
      // Пустое поле — «очистить»: null проходит валидацию (пустая строка
      // в e-mail — нет). Без права на личные данные адреса и контакта ЧП
      // нет в форме — и в запросе тоже.
      const picked = canSensitive ? values : { phone: values.phone, email: values.email };
      const payload = Object.fromEntries(
        Object.entries(picked).map(([key, value]) => [key, value?.trim() ? value.trim() : null]),
      );
      return (await api.patch(`/employees/${employee.id}`, payload)).data;
    },
    { successMessage: t('Контакты сохранены'), invalidate: [['employee'], ['employees'], ['driver']] },
  );

  return (
    <>
      <SectionHeader
        title={t('Контакты')}
        extra={
          canEdit && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={save.isPending}
              onClick={() => void form.validateFields().then((values) => save.mutate(values))}
            >
              {t('Сохранить')}
            </Button>
          )
        }
      />
      <Form form={form} layout="vertical" disabled={!canEdit} style={{ maxWidth: 820 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="phone" label={t('Телефон')}>
              <Input placeholder="+998 90 123-45-67" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="email" label="E-mail" rules={[{ type: 'email', message: t('Неверный адрес') }]}>
              <Input placeholder="name@company.uz" />
            </Form.Item>
          </Col>
        </Row>
        {canSensitive && (
          <>
            <Form.Item name="address" label={t('Адрес проживания')}>
              <Input.TextArea rows={2} maxLength={300} />
            </Form.Item>
            <Divider orientation="left" plain style={{ marginTop: 4 }}>
              {t('На случай чрезвычайной ситуации')}
            </Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="emergencyContactName" label={t('Кому звонить (ФИО, кем приходится)')}>
                  <Input placeholder={t('Например: Иванова Мария, жена')} maxLength={120} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="emergencyContactPhone" label={t('Телефон')}>
                  <Input placeholder="+998 90 123-45-67" maxLength={40} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </>
  );
}
